import { Box, Button, Card, CardContent, CardHeader, FormHelperText, Grid, Stack, Drawer, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { useFormik } from "formik";
import { useRecordContext } from "ra-core";
import { FC, useCallback, useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import * as Yup from 'yup'
import OdForm, { OnSubmit } from "@/components/formik/OdForm";
import TextField from "@/components/formik/TextField";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { ClientManageDto, ClientStatus, ClientType } from "@/gen/api/user";
import { useUserTimeFormat } from "@/utils/userTimeFormat";
import { useSnackbar } from "notistack";
import retrieveAxiosErr from "@/utils/retrieveAxiosErr";
import ReadonlyField from "@/components/input/ReadonlyField";
import ServerEditionSecureFileTag from "./EditClient.ServerEditionSecureFileTag";


interface EditClientProps {
  open: boolean;
  onClose: () => void;
}

const validationSchema =
  Yup.object().shape({
    ClientName: Yup.string().max(200).required('Client Name is required'),
    Remark: Yup.string().max(512),
  })

const EditClient: FC<EditClientProps> = ({
  open,
  onClose
}) => {
  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480
  
  const save = useSimpleSaveAction()
  const record = useRecordContext<ClientManageDto>()
  const { enqueueSnackbar } = useSnackbar()

  const initialValues = {
    submit: '',
    status: null as ClientStatus|null,
    ClientName: record?.clientName || '',
    Remark: record?.remark || '',
  }

  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    const options = {
      onSuccess: () => {
        setStatus({ success: true })
        setSubmitting(false)
        onClose()
        if (values.status === ClientStatus.Rejected) {
          enqueueSnackbar(`Revoke Client Access succeed.`, { variant: 'success' })
        } else {
          enqueueSnackbar(`Save Client Info succeed.`, { variant: 'success' })
        }
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
        const errMsg = retrieveAxiosErr(err)
        if (values.status === ClientStatus.Rejected) {
          enqueueSnackbar(`Revoke Client Access failed: ${errMsg}`, { variant: 'error' })
        } else {
          enqueueSnackbar(`Save Client Info failed: ${errMsg}`, { variant: 'error' })
        }
      }
    }
    const payload = {
      status: values.status,
      prevStatus: record?.status,
      ...(values.ClientName && values.ClientName !== record.clientName && {
        clientName: values.ClientName,
      }),
      ...(values.Remark !== record.remark && {
        remark: values.Remark,
      }),
    }

    save(payload, options)
  }, [save, record, enqueueSnackbar, onClose])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { errors, values, isSubmitting, handleSubmit, setFieldValue, resetForm } = formikCtrl

  const format = useUserTimeFormat()

  const getDateString = (srcStr?: string|null) => {
    const dateVal = (srcStr || '') as string
    const dateString = dateVal ? format(new Date(dateVal), 'Pp') : ''
    return dateString
  }

  useEffect(() => {
    resetForm({
      values: {
        submit: '',
        status: null as ClientStatus|null,
        ClientName: record?.clientName || '',
        Remark: record?.remark || '',
      }
    })
  }, [record, resetForm])

  const hasModification = 
    values.ClientName !== record?.clientName ||
    (values.Remark !== record?.remark && (Boolean(values.Remark) || Boolean(record?.remark)))

  const viewDetail = record && (record.status === ClientStatus.Rejected || record.status === ClientStatus.Revoked)

  return (
    <Drawer
      anchor="right"
      // onOpen={() => {}}
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          p: 2,
          width: width,
        }
      }}
    >
      {record &&
        <OdForm formikCtrl={formikCtrl}>
          {open &&
            <Helmet>
              <title>{viewDetail ? "Client Detail" : "Edit Client"} | OnData</title>
            </Helmet>}
          <Card>
            <CardHeader title={viewDetail ? "Client Detail" : "Edit Client"} />
            <CardContent>
              <Grid
                container
                spacing={4}
              >
                <Grid item xs={12} >
                  <TextField label="Client Name*" name="ClientName" />
                </Grid>
                <Grid item xs={12} >
                  <ReadonlyField label="Client Id" value={record?.clientId ?? ''} />
                </Grid>
                <Grid item xs={6} >
                  <Typography variant="body2">
                    {record.clientType === ClientType.DataDriver ? 'Data Driver'
                      : record.clientType === ClientType.EncryptionTool ? 'Encryption Tool'
                      : record.clientType === ClientType.ShareSecretDataDriver ? 'Shared Secret Data Driver'
                      : record.clientType === ClientType.ShareSecretEncryptionTool ? 'Shared Secret Encryption Tool'
                      : record.clientType === ClientType.SecureFileApp ? 'SecureFile App'
                      : null
                    }
                  </Typography>
                  <Typography variant="caption" component="p" color="success.main">
                    {record.softwareVersion}
                  </Typography>
                </Grid>
                <Grid item xs={6} >
                  <Typography variant="body2">
                    {getDateString(record.registerAt)}
                  </Typography>
                  <Tooltip title={record.r1Username || ''}>
                    <Box>
                      <Typography variant="caption">
                        {record.r1Username 
                        ? <span>Registered By <Typography variant="caption" color="primary.main">
                            {record.r1UserFirstName} {record.r1UserLastName}
                          </Typography></span>
                        : "Registered"  
                      }
                      </Typography>                
                    </Box>
                  </Tooltip>
                </Grid>
                <Grid item xs={6} >
                  <Typography variant="body2">
                    {record.osInfo}<br/>
                    <Typography variant="caption">
                      {record.ipAddress}
                    </Typography>
                  </Typography>
                </Grid>
                {record.reviewedAt &&
                  <Grid item xs={6} >
                    <Typography variant="body2">
                      {getDateString(record.reviewedAt)}
                    </Typography>
                    <Tooltip title={record.r2Username || ''}>
                      <Box>
                        <Typography variant="caption">
                          Approved By <Typography variant="caption" color="primary.main">
                            {record.r2UserFirstName} {record.r2UserLastName}
                          </Typography>
                        </Typography>                
                      </Box>
                    </Tooltip>
                  </Grid>}
                {record.status === ClientStatus.Suspend &&
                  <Grid item xs={6} sx={{ ml: 'auto' }} >
                    <Typography variant="body2">
                      {getDateString(record.suspendedAt)}
                    </Typography>
                    <Tooltip title={record.r4Username || ''}>
                      <Box>
                        <Typography variant="caption">
                          Suspended By <Typography variant="caption" color="primary.main">
                            {record.r4UserFirstName} {record.r4UserLastName}
                          </Typography>
                        </Typography>                
                      </Box>
                    </Tooltip>
                  </Grid>}
                {record.clientType === ClientType.SecureFileApp &&
                  <ServerEditionSecureFileTag record={record} />
                }
                <Grid item xs={12} >
                  <TextField label="Remark" name="Remark" multiline={true} rows={4} />
                </Grid>
              </Grid>
              {errors.submit && (
                <Box sx={{ mt: 3 }}>
                  <FormHelperText error>
                    {errors.submit}
                  </FormHelperText>
                </Box>
              )}
            </CardContent>
            <Stack
              direction="row"
              sx={{ p: 2 }}
            >
              <Button
                color="secondary"
                disabled={!hasModification || isSubmitting}
                type="submit"
                variant="outlined"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.preventDefault()
                  setTimeout(() => handleSubmit())
                  return false
                }}
              >
                Save
              </Button>
              {!viewDetail &&
                <>
                  <Button
                    color="primary"
                    disabled={isSubmitting}
                    sx={{ mr: 'auto', ml: 2 }}
                    type="submit"
                    variant="contained"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      setFieldValue('status', record.status === ClientStatus.Enabled ? ClientStatus.Suspend : ClientStatus.Enabled)
                      e.preventDefault()
                      setTimeout(() => handleSubmit())
                      return false
                    }}
                  >
                    {record.status === ClientStatus.Enabled
                      ? (hasModification ? "Save & Suspend" : "Suspend")
                      : (hasModification ? "Save & Resume" : "Resume")}
                  </Button>
                  <Button
                    color="error"
                    disabled={isSubmitting}
                    type="submit"
                    variant="contained"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      setFieldValue('status', ClientStatus.Revoked)
                      e.preventDefault()
                      setTimeout(() => handleSubmit())
                      return false
                    }}
                  >
                    {hasModification ? "Save & Revoke" : "Revoke"}
                  </Button>
                </>
              }
            </Stack>
          </Card>

        </OdForm>
      }
    </Drawer>
  )
}

export default EditClient;
